import { useState, useEffect } from 'react'
import MFEBus from '../mfe/MFEBus'
import { mfeRegistry } from '../mfe/MFERegistry'
import { useMFEStore } from '../mfe/hooks'
/**
 * MFEDevPanel — floating DevTools overlay.
 * Three tabs:
 *   mfe   — live status of every registered micro frontend
 *   bus   — real-time event stream from MFEBus
 *   store — shared MFEStore state snapshot
 *
 * The floating badge turns red if any MFE has errored.
 */
export function MFEDevPanel() {
  const [open,  setOpen]  = useState(false)
  const [mfes,  setMfes]  = useState([])
  const [log,   setLog]   = useState([])
  const [tab,   setTab]   = useState('mfe')
  const [store] = useMFEStore()

  useEffect(() => {
    const refresh = () => setMfes([...mfeRegistry.values()])
    const off1 = MFEBus.on('mfe:mounted',   refresh)
    const off2 = MFEBus.on('mfe:unmounted', refresh)
    const off3 = MFEBus.on('mfe:error',     refresh)
    const off4 = MFEBus.onLog(entries => { refresh(); setLog(entries) })
    refresh()
    return () => { off1(); off2(); off3(); off4() }
  }, [])

  const mounted  = mfes.filter(m => m.status === 'mounted').length
  const errored  = mfes.filter(m => m.status === 'error').length
  const badgeCol = errored > 0 ? '#ef4444' : 'var(--lm)'
  const pill     = (color, bg) => ({ fontFamily:'var(--mono)', fontSize:'.58rem', padding:'.08rem .38rem', borderRadius:'4px', background: bg || `${color}18`, color, border:`1px solid ${color}40`, flexShrink:0 })

  return (<>
    <button onClick={() => setOpen(o => !o)} style={{ position:'fixed', bottom:'5rem', right:'1.5rem', zIndex:210, display:'flex', alignItems:'center', gap:'.45rem', padding:'.38rem .9rem', borderRadius:'99px', background:'rgba(3,3,8,.88)', border:`1px solid ${badgeCol}40`, backdropFilter:'blur(16px)', color:badgeCol, fontFamily:'var(--mono)', fontSize:'.68rem', fontWeight:500, cursor:'pointer', boxShadow:`0 0 18px ${badgeCol}20`, transition:'all .2s' }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:badgeCol, boxShadow:`0 0 7px ${badgeCol}`, display:'inline-block', animation: mounted > 0 ? 'pdot-pulse 2s ease-in-out infinite' : 'none' }}/>
      {mounted}/{mfes.length} MFEs{errored > 0 ? ` · ${errored} ⚠` : ''}
    </button>

    {open && (
      <div style={{ position:'fixed', bottom:'7rem', right:'1.5rem', zIndex:210, width:'min(380px,calc(100vw - 2rem))', background:'rgba(3,3,10,.95)', border:'1px solid rgba(255,255,255,.1)', borderRadius:'18px', backdropFilter:'blur(32px)', boxShadow:'0 20px 60px rgba(0,0,0,.7)', overflow:'hidden', fontFamily:'var(--mono)' }}>
        {/* header */}
        <div style={{ padding:'.7rem 1rem', borderBottom:'1px solid rgba(255,255,255,.07)', display:'flex', alignItems:'center', gap:'.75rem' }}>
          <span style={{ fontSize:'.72rem', color:'var(--cy)', fontWeight:600, letterSpacing:'.04em', flex:1 }}>⬡ MFE DevTools</span>
          {['mfe', 'bus', 'store'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{ background: tab===t ? 'rgba(0,212,255,.12)' : 'none', border:`1px solid ${tab===t ? 'rgba(0,212,255,.3)' : 'transparent'}`, borderRadius:'6px', color: tab===t ? 'var(--cy)' : 'var(--mu)', fontSize:'.65rem', padding:'.18rem .55rem', cursor:'pointer', fontFamily:'var(--mono)', transition:'all .15s' }}>{t}</button>
          ))}
          <button onClick={() => setOpen(false)} style={{ background:'none', border:'none', color:'var(--mu)', cursor:'pointer', fontSize:'1.1rem', lineHeight:1, padding:'0 .1rem' }}>×</button>
        </div>

        {tab === 'mfe' && (
          <div style={{ padding:'.6rem .75rem', maxHeight:'260px', overflowY:'auto' }}>
            {mfes.map(m => {
              const col = m.status==='mounted' ? 'var(--lm)' : m.status==='error' ? '#ef4444' : '#4a5180'
              return (
                <div key={m.name} style={{ display:'flex', alignItems:'center', gap:'.55rem', padding:'.42rem .55rem', borderRadius:'9px', marginBottom:'.22rem', background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.05)' }}>
                  <span style={{ width:7, height:7, borderRadius:'50%', flexShrink:0, background:col, boxShadow:`0 0 6px ${col}90` }}/>
                  <span style={{ fontSize:'.68rem', color:'var(--tx)', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.name}</span>
                  <span style={{ fontSize:'.6rem', color:'var(--mu)', flexShrink:0 }}>v{m.version}</span>
                  <span style={pill(col)}>{m.status}</span>
                  {m.team && <span style={pill('var(--mu)')}>{m.team}</span>}
                </div>
              )
            })}
          </div>
        )}

        {tab === 'bus' && (
          <div style={{ padding:'.6rem .75rem', maxHeight:'260px', overflowY:'auto' }}>
            <div style={{ fontSize:'.6rem', color:'var(--mu)', letterSpacing:'.06em', textTransform:'uppercase', marginBottom:'.5rem' }}>Live Event Stream</div>
            {log.length === 0 && <div style={{ color:'var(--mu)', fontSize:'.7rem', opacity:.5, textAlign:'center', padding:'.5rem' }}>Waiting for events…</div>}
            {log.map((e, i) => (
              <div key={i} style={{ display:'flex', gap:'.5rem', padding:'.3rem 0', borderBottom:'1px solid rgba(255,255,255,.04)', opacity: Math.max(.25, 1 - i * .055) }}>
                <span style={{ color:'var(--mu)', flexShrink:0, fontSize:'.6rem' }}>{e.ts}</span>
                <span style={{ color:'var(--cy)', fontSize:'.65rem', flexShrink:0 }}>{e.event}</span>
                {e.payload?.name    && <span style={{ color:'var(--lm)',   fontSize:'.65rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.payload.name}</span>}
                {e.payload?.message && <span style={{ color:'#fca5a5',    fontSize:'.62rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.payload.message}</span>}
              </div>
            ))}
          </div>
        )}

        {tab === 'store' && (
          <div style={{ padding:'.75rem 1rem', maxHeight:'260px', overflowY:'auto' }}>
            <div style={{ fontSize:'.6rem', color:'var(--mu)', letterSpacing:'.06em', textTransform:'uppercase', marginBottom:'.6rem' }}>Shared State Snapshot</div>
            {Object.entries(store).map(([k, v]) => (
              <div key={k} style={{ display:'flex', gap:'1rem', padding:'.35rem 0', borderBottom:'1px solid rgba(255,255,255,.04)' }}>
                <span style={{ fontSize:'.68rem', color:'var(--cy)', minWidth:'120px' }}>{k}</span>
                <span style={{ fontSize:'.68rem', color:'var(--lm)' }}>{JSON.stringify(v)}</span>
              </div>
            ))}
          </div>
        )}

        <div style={{ padding:'.5rem 1rem', borderTop:'1px solid rgba(255,255,255,.06)', fontSize:'.6rem', color:'var(--mu)', display:'flex', justifyContent:'space-between' }}>
          <span>React micro-frontend pattern</span>
          <span style={{ color:'var(--cy)' }}>{mfes.length} registered</span>
        </div>
      </div>
    )}
  </>)
}
